(function () {
  if (customElements.get('zoorian-warenwert')) {
    return;
  }

  const ROOT_URL =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const GIFT_PROPERTY = '_zoorian_geschenk';

  const SOURCE = 'zoorian-warenwert';

  const instances = new Set();

  let busy = false;
  let queued = false;

  function setHidden(element, state) {
    if (!element) return;

    if (state) element.setAttribute('hidden', '');
    else element.removeAttribute('hidden');
  }

  function isGift(item) {
    return Boolean(item && item.properties && item.properties[GIFT_PROPERTY]);
  }

  function countItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((sum, item) => (isGift(item) ? sum : sum + item.quantity), 0);
  }

  async function readCart() {
    const response = await fetch(`${ROOT_URL}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`[zoorian-warenwert] cart.js nicht lesbar (${response.status})`);
    }

    return response.json();
  }

  async function writeCart(path, body) {
    const response = await fetch(`${ROOT_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`[zoorian-warenwert] ${path} fehlgeschlagen (${response.status})`);
    }

    return response.json();
  }

  function notifyCart(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
    }

    refreshCartBubble();
  }

  async function refreshCartBubble() {
    const bubble = document.getElementById('cart-icon-bubble');

    if (!bubble) return;

    try {
      const response = await fetch(`${ROOT_URL}?section_id=cart-icon-bubble`, {
        credentials: 'same-origin',
      });

      if (!response.ok) return;

      const markup = await response.text();
      const source = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('.shopify-section');

      if (source) bubble.innerHTML = source.innerHTML;
    } catch (error) {}
  }

  function giftThreshold() {
    for (const instance of instances) {
      if (instance.giftThreshold > 0) return instance.giftThreshold;
    }

    return 0;
  }

  async function enforceGift(cart) {
    const threshold = giftThreshold();

    if (threshold < 1) return cart;

    const giftLines = cart.items.filter(isGift);

    if (!giftLines.length) return cart;

    let next = cart;

    if (countItems(cart) < threshold) {
      for (const line of giftLines) {
        next = await writeCart('cart/change.js', { id: line.key, quantity: 0 });
      }

      notifyCart(next);

      return next;
    }

    for (let index = 1; index < giftLines.length; index += 1) {
      next = await writeCart('cart/change.js', { id: giftLines[index].key, quantity: 0 });
    }

    if (giftLines[0].quantity > 1) {
      next = await writeCart('cart/change.js', { id: giftLines[0].key, quantity: 1 });
    }

    if (next !== cart) notifyCart(next);

    return next;
  }

  async function refresh() {
    if (busy) {
      queued = true;
      return;
    }

    busy = true;

    try {
      let cart = await readCart();

      cart = await enforceGift(cart);

      instances.forEach((instance) => instance.sync(cart));
    } catch (error) {
    } finally {
      busy = false;

      if (queued) {
        queued = false;
        refresh();
      }
    }
  }

  class ZoorianWarenwert extends HTMLElement {
    connectedCallback() {
      this.headline = this.querySelector('[data-zwt-headline]');

      this.textProgress = this.dataset.textProgress || '';
      this.textDone = this.dataset.textDone || '';

      this.tiers = Array.from(this.querySelectorAll('[data-zwt-tier]')).map((element, index) => ({
        element,
        threshold: parseInt(element.dataset.threshold, 10) || index + 1,
        label: element.dataset.label || '',
      }));

      if (!this.tiers.length) return;

      instances.add(this);

      this.setupGift();
      this.update(parseInt(this.dataset.count, 10) || 0);

      if (this.gift) refresh();
    }

    disconnectedCallback() {
      instances.delete(this);

      window.clearTimeout(this.modalTimer);

      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);

      if (this.modal && this.modal.parentElement === document.body) {
        this.modal.remove();
        document.body.classList.remove('overflow-hidden');
      }
    }

    headlineFor(count) {
      const next = this.tiers.find((tier) => count < tier.threshold);

      if (!next) return this.textDone;

      return this.textProgress
        .replace('[anzahl]', String(next.threshold - count))
        .replace('[belohnung]', next.label);
    }

    progressFor(count) {
      const steps = this.tiers.length;
      const segment = 100 / steps;
      const half = segment / 2;

      const reached = this.tiers.filter((tier) => count >= tier.threshold).length;

      if (reached >= steps) return 100;

      if (reached === 0) {
        const first = this.tiers[0].threshold;

        return first > 0 ? Math.min((count / first) * half, half) : 0;
      }

      const last = this.tiers[reached - 1].threshold;
      const next = this.tiers[reached].threshold;
      const span = next - last;
      const base = reached * segment - half;

      return span > 0 ? base + ((count - last) / span) * segment : base;
    }

    update(count) {
      if (typeof count !== 'number' || Number.isNaN(count)) return;

      const reached = this.tiers.filter((tier) => count >= tier.threshold).length;

      this.tiers.forEach((tier, index) => {
        tier.element.classList.toggle('is-reached', index < reached);
        tier.element.classList.toggle('is-current', index === reached - 1);
      });

      this.style.setProperty('--zwt-progress', `${this.progressFor(count).toFixed(2)}%`);

      if (!this.headline) return;

      const text = this.headlineFor(count);

      this.headline.textContent = text;
      this.headline.hidden = text.trim() === '';
    }

    sync(cart) {
      const count = countItems(cart);

      this.update(count);

      if (!this.gift) return;

      this.renderGift(count, cart.items.find(isGift) || null);
    }

    setupGift() {
      if (this.giftReady) {
        if (this.modal) document.body.appendChild(this.modal);

        document.addEventListener('keydown', this.onKeydown);
        return;
      }

      this.giftTrigger = this.querySelector('[data-zwt-gift-trigger]');
      this.gift = this.giftTrigger && this.giftTrigger.closest('[data-zwt-tier]');
      this.giftThreshold = parseInt(this.dataset.giftThreshold, 10) || 0;

      if (!this.gift || this.giftThreshold < 1) {
        this.gift = null;
        return;
      }

      this.textGiftHint = this.dataset.textGiftHint || '';
      this.textGiftChosen = this.dataset.textGiftChosen || '';

      this.giftHint = this.gift.querySelector('[data-zwt-gift-hint]');
      this.giftHintText = this.gift.querySelector('[data-zwt-gift-hint-text]');
      this.giftIcon = this.gift.querySelector('[data-zwt-gift-icon]');
      this.giftCheck = this.gift.querySelector('[data-zwt-gift-check]');

      this.modal = this.querySelector('[data-zwt-gift-modal]');
      this.modalRemove = this.modal && this.modal.querySelector('[data-zwt-gift-remove]');
      this.modalDialog = this.modal && this.modal.querySelector('.zoorian-gift-modal__dialog');
      this.modalError = this.modal && this.modal.querySelector('[data-zwt-gift-error]');
      this.cards = this.modal ? Array.from(this.modal.querySelectorAll('[data-zwt-gift-card]')) : [];

      this.onClickBound = this.onClick.bind(this);
      this.addEventListener('click', this.onClickBound);

      if (this.modal) {
        this.modal.style.cssText = this.getAttribute('style') || '';
        this.modal.addEventListener('click', this.onClickBound);

        document.body.appendChild(this.modal);
      }

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal();
      };

      document.addEventListener('keydown', this.onKeydown);

      this.giftReady = true;
    }

    onClick(event) {
      const trigger = event.target.closest('[data-zwt-gift-trigger]');

      if (trigger) {
        event.preventDefault();
        this.openModal(trigger);
        return;
      }

      if (event.target.closest('[data-zwt-gift-close]')) {
        event.preventDefault();
        this.closeModal();
        return;
      }

      const choose = event.target.closest('[data-zwt-gift-choose]');

      if (choose) {
        event.preventDefault();
        this.chooseGift(choose);
        return;
      }

      if (event.target.closest('[data-zwt-gift-remove]')) {
        event.preventDefault();
        this.removeGift();
      }
    }

    renderGift(count, line) {
      const unlocked = count >= this.giftThreshold;

      this.gift.classList.toggle('is-calling', unlocked && !line);
      this.gift.classList.toggle('is-chosen', Boolean(line));

      if (this.giftTrigger) this.giftTrigger.disabled = !unlocked;

      setHidden(this.giftHint, !unlocked);

      if (this.giftHintText) {
        this.giftHintText.textContent = line
          ? this.textGiftChosen.replace('[produkt]', line.product_title)
          : this.textGiftHint;
      }

      setHidden(this.giftIcon, Boolean(line));
      setHidden(this.giftCheck, !line);

      setHidden(this.modalRemove, !line);

      this.cards.forEach((card) => {
        card.classList.toggle('is-chosen', Boolean(line) && String(card.dataset.productId) === String(line.product_id));
      });
    }

    openModal(trigger) {
      if (!this.modal) return;

      this.returnFocusTo = trigger || null;

      window.clearTimeout(this.modalTimer);

      document.body.appendChild(this.modal);

      this.modal.hidden = false;
      void this.modal.offsetWidth;

      this.modal.classList.add('is-open');
      document.body.classList.add('overflow-hidden');

      if (this.modalError) this.modalError.hidden = true;

      if (typeof trapFocus === 'function' && this.modalDialog) {
        trapFocus(this.modalDialog, this.modalDialog);
      } else if (this.modalDialog) {
        this.modalDialog.focus();
      }
    }

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');

      window.clearTimeout(this.modalTimer);

      this.modalTimer = window.setTimeout(() => {
        if (this.modal) this.modal.hidden = true;
      }, 300);

      const target =
        this.returnFocusTo && this.returnFocusTo.isConnected ? this.returnFocusTo : null;

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(target);
      } else if (target) {
        target.focus();
      }
    }

    setBusy(state) {
      if (this.modal) this.modal.classList.toggle('is-busy', state);

      [this, this.modal].forEach((scope) => {
        if (!scope) return;

        scope.querySelectorAll('[data-zwt-gift-choose], [data-zwt-gift-remove]').forEach((button) => {
          button.disabled = state;
        });
      });
    }

    async chooseGift(button) {
      const card = button.closest('[data-zwt-gift-card]');
      const select = card && card.querySelector('[data-zwt-gift-variant]');
      const variantId = select ? select.value : button.dataset.variantId;

      if (!variantId) return;

      this.setBusy(true);

      try {
        let cart = await readCart();

        for (const line of cart.items.filter(isGift)) {
          await writeCart('cart/change.js', { id: line.key, quantity: 0 });
        }

        await writeCart('cart/add.js', {
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: { [GIFT_PROPERTY]: String(this.giftThreshold) },
            },
          ],
        });

        cart = await readCart();

        notifyCart(cart);
        instances.forEach((instance) => instance.sync(cart));

        this.closeModal();
      } catch (error) {
        if (this.modalError) this.modalError.hidden = false;
      } finally {
        this.setBusy(false);
      }
    }

    async removeGift() {
      this.setBusy(true);

      try {
        let cart = await readCart();

        for (const line of cart.items.filter(isGift)) {
          cart = await writeCart('cart/change.js', { id: line.key, quantity: 0 });
        }

        notifyCart(cart);
        instances.forEach((instance) => instance.sync(cart));
      } catch (error) {
      } finally {
        this.setBusy(false);
      }
    }
  }

  customElements.define('zoorian-warenwert', ZoorianWarenwert);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === SOURCE) return;

      refresh();
    });
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
})();
